import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './ReviewFormComponent.css';
import axios from 'axios';
import { selectUser, selectIsUserLoggedIn } from '../../redux/WithcookieSlice';
import { Link } from 'react-router-dom';
import { useTranslation } from '../../redux/TranslationHelper';

const ReviewFormComponent = ({ id, componentProps = {}, style = {}, isPreview = false }) => {
  const { t } = useTranslation();
  
  const { 
    className = "review-form-wrapper",
    title = t("상품 리뷰 작성"),
    productId = null,
    orderId = null,
    showLoginMessage = true,
    loginRedirectUrl = "/login",
    placeholderText = t("상품에 대한 경험과 생각을 자유롭게 작성해주세요."),
    submitButtonText = t("리뷰 등록")
  } = componentProps || {};
  
  const dispatch = useDispatch();
  const currentUser = useSelector(selectUser);
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);
  
  const [reviewData, setReviewData] = useState({
    content: '',
    rating: 5,
    images: []
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    // 컴포넌트 마운트시 초기화
    setReviewData({
      content: '',
      rating: 5,
      images: []
    });
    setError(null);
    setSuccess(false);
  }, []);
  
  // 별점 변경 핸들러
  const handleRatingChange = (rating) => {
    setReviewData(prev => ({ ...prev, rating }));
  };
  
  // 내용 변경 핸들러
  const handleContentChange = (e) => {
    setReviewData(prev => ({ ...prev, content: e.target.value }));
  };
  
  // 이미지 업로드 핸들러
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + reviewData.images.length > 5) {
      setError(t('이미지는 최대 5개까지만 업로드할 수 있습니다.'));
      return;
    }
    
    const promises = files.map(file => uploadImage(file));
    
    Promise.all(promises)
      .then(uploadedImages => {
        setReviewData(prev => ({
          ...prev,
          images: [...prev.images, ...uploadedImages]
        }));
        setError(null);
      })
      .catch(err => {
        setError(t('이미지 업로드 중 오류가 발생했습니다.'));
        console.error(t('이미지 업로드 실패:'), err);
      });
  };
  
  // 이미지 삭제 핸들러
  const handleRemoveImage = (index) => {
    setReviewData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };
  
  // 이미지 업로드 함수
  const uploadImage = (file) => {
    return new Promise(async (resolve, reject) => {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('bucket', 'withcookie-bucket');
        formData.append('path', 'c/reviews');

        const res = await axios.post(
          `https://refrigerator.logipasta.com/v1/file`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Access-Control-Allow-Origin': '*',
            },
          }
        );

        if (res.data && res.data.file) {
          resolve(res.data.file);
        } else {
          reject(t('이미지 URL을 가져올 수 없습니다.'));
        }
      } catch (err) {
        console.error(t('이미지 업로드 실패:'), err);
        reject(err);
      }
    });
  };
  
  // 리뷰 제출 함수
  const handleSubmitReview = async () => {
    if (!reviewData.content.trim()) {
      setError(t('리뷰 내용을 입력해주세요.'));
      return;
    }
    
    if (!productId && !isPreview) {
      setError(t('상품 정보가 없습니다.'));
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    // 미리보기 모드일 경우 API 호출 없이 성공 처리
    if (isPreview) {
      setTimeout(() => {
        setSuccess(true);
        setIsSubmitting(false);
        setReviewData({
          content: '',
          rating: 5,
          images: []
        });
      }, 1000);
      return;
    }
    
    try {
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://api.withcookie.co.kr';
      
      const response = await axios.post(
        `${apiBaseUrl}/api/review`, 
        {
          productId: productId,
          orderId: orderId,
          content: reviewData.content,
          rating: reviewData.rating,
          images: reviewData.images
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('userToken')}`
          }
        }
      );
      
      setSuccess(true);
      setReviewData({
        content: '',
        rating: 5,
        images: []
      });
    } catch (err) {
      console.error(t('리뷰 제출 실패:'), err);
      setError(err.response?.data?.message || t('리뷰 제출 중 오류가 발생했습니다.'));
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // 로그인 필요
  if (!isUserLoggedIn && !isPreview && showLoginMessage) {
    return (
      <div className={className} id={id} style={style}>
        <div className="review-form-login-required">
          <h2>{title}</h2>
          <p>{t('리뷰를 작성하려면 로그인이 필요합니다.')}</p>
          <Link to={loginRedirectUrl} className="review-login-button">{t('로그인')}</Link>
        </div>
      </div>
    );
  }
  
  // 성공 메시지
  if (success) {
    return (
      <div className={className} id={id} style={style}>
        <div className="review-form-success">
          <h2>{t('리뷰가 등록되었습니다')}</h2>
          <p>{t('소중한 리뷰를 작성해주셔서 감사합니다.')}</p>
          <button className="review-form-button" onClick={() => setSuccess(false)}>
            {t('새 리뷰 작성하기')}
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className={className} id={id} style={style}>
      <div className="review-form-container">
        <h2 className="review-form-title">{title}</h2>
        
        <div className="review-form-content">
          <div className="rating-section">
            <h3>{t('별점')}</h3>
            <div className="star-rating">
              {[1, 2, 3, 4, 5].map((star) => (
                <span
                  key={star}
                  className={`star ${star <= reviewData.rating ? 'filled' : ''}`}
                  onClick={() => handleRatingChange(star)}
                >
                  ★
                </span>
              ))}
            </div>
          </div>
          
          <div className="content-section">
            <h3>{t('리뷰 내용')}</h3>
            <textarea
              value={reviewData.content}
              onChange={handleContentChange}
              placeholder={placeholderText}
            />
          </div>
          
          {/* <div className="image-section">
            <h3>{t('사진 첨부 (최대 5장)')}</h3>
            <div className="image-upload">
              <input
                type="file"
                id={`review-image-upload-${id}`}
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                disabled={reviewData.images.length >= 5}
              />
              <label htmlFor={`review-image-upload-${id}`} className={reviewData.images.length >= 5 ? 'disabled' : ''}>
                {t('이미지 선택')}
              </label>
              <span className="image-count">{reviewData.images.length}/5</span>
            </div>
            
            {reviewData.images.length > 0 && (
              <div className="image-preview-container">
                {reviewData.images.map((image, index) => (
                  <div key={index} className="image-preview">
                    <img src={image} alt={t(`리뷰 이미지 ${index + 1}`)} />
                    <button
                      className="remove-image"
                      onClick={() => handleRemoveImage(index)}
                      title={t("이미지 삭제")}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div> */}
          
          {error && <div className="error-message">{error}</div>}
          
          <div className="review-form-actions">
            <button 
              className="review-form-submit" 
              onClick={handleSubmitReview}
              disabled={isSubmitting}
            >
              {isSubmitting ? t('제출 중...') : submitButtonText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewFormComponent; 